
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionStatus } from './types';

export async function checkSubscriptionStatus(priceId?: string): Promise<SubscriptionStatus> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return {
        loading: false,
        active: false,
        subscribed: false,
        onTrial: false,
        error: null,
        subscriptions: []
      };
    }

    const { data, error } = await supabase.functions.invoke('check-subscription', {
      body: { priceId }
    });

    if (error) throw error;

    // Return data with explicit subscription data
    return {
      loading: false,
      active: data.active || false,
      subscribed: data.subscribed || false,
      onTrial: data.onTrial || false,
      error: null,
      subscriptions: data.subscriptions || []
    };
  } catch (error: any) {
    console.error("Error checking subscription:", error);
    return {
      loading: false,
      active: false,
      subscribed: false,
      onTrial: false,
      error: error.message || "Failed to check subscription",
      subscriptions: []
    };
  }
}

export async function createCheckoutSession(
  priceId: string, 
  successUrl?: string,
  options: {
    isExtendedTrial?: boolean;
    discountPercentage?: number;
  } = {}
): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("User is not authenticated");
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { 
        priceId,
        successUrl,
        isExtendedTrial: options.isExtendedTrial, // Pass the extended trial flag
        discountPercentage: options.discountPercentage // Pass the discount percentage
      }
    });

    if (error) throw error;
    if (!data?.url) {
      throw new Error("No checkout URL returned");
    }

    return data.url;
  } catch (error: any) {
    console.error("Error creating checkout session:", error);
    throw new Error(error.message || "Failed to create checkout session");
  }
}

export async function createPortalSession(returnUrl?: string): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("User is not authenticated");
    }

    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: { returnUrl }
    });

    if (error) throw error;
    if (!data?.url) {
      throw new Error("No portal URL returned");
    }

    return data.url;
  } catch (error: any) {
    console.error("Error creating portal session:", error);
    throw new Error(error.message || "Failed to create portal session");
  }
}
